import { useState, useEffect } from "react";
import { supabaseFunctions } from "@/lib/supabase";
import { useBogotaTime } from "./use-bogota-time";

export function useFXRate() {
  const [rate, setRate] = useState<number>(4200); // Fallback rate
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMarketClosed, setIsMarketClosed] = useState(false);
  const { currentTime } = useBogotaTime();

  // Check if market is open based on Colombia time (8:00 AM - 3:30 PM)
  const isMarketOpen = () => {
    const now = new Date();
    // Convert to Colombia timezone
    const colombiaTime = new Date(
      now.toLocaleString("en-US", { timeZone: "America/Bogota" })
    );
    const hour = colombiaTime.getHours();
    const minute = colombiaTime.getMinutes();

    // Market is open from 8:00 AM to 3:30 PM (15:30)
    const isAfterOpen = hour > 8 || (hour === 8 && minute >= 0);
    const isBeforeClose = hour < 15 || (hour === 15 && minute <= 30);

    return isAfterOpen && isBeforeClose;
  };


  const fetchRate = async () => {
    setIsLoading(true);
    setError(null);

    setIsMarketClosed(false);

    try {
      console.log("Fetching rate from get-seticap-rate function");

      const { data, error: funcError } =
        await supabaseFunctions.functions.invoke("get-seticap-rate");

      if (funcError) {
        console.error("Error calling get-seticap-rate:", funcError);

        // Check for non-2xx status code errors which usually indicate 503 (service unavailable)
        if (
          funcError.message &&
          (funcError.message.includes("503") ||
            funcError.message.includes("non-2xx status code") ||
            funcError.name === "FunctionsHttpError")
        ) {
          console.log("Market is closed (503/non-2xx error detected)");
          setIsMarketClosed(true);
          setError("Mercado cerrado");
        } else {
          setError("Error al obtener la tasa");
        }
        return;
      }

      if (data && data.offRamp) {
        console.log("Rate fetched successfully:", data.offRamp);
        setRate(data.offRamp);
        setIsMarketClosed(false);
      } else {
        console.warn("No offRamp found in response:", data);
        setError("Formato de respuesta inválido");
      }
    } catch (error) {
      console.error("Error fetching rate:", error);
      setError("Error de conexión");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Fetch rate immediately
    fetchRate();

    // Set up interval to refresh rate every 30 seconds
    const interval = setInterval(fetchRate, 30000);

    return () => clearInterval(interval);
  }, []);

  // Check market hours whenever time changes
  useEffect(() => {
    if (!isMarketOpen()) {
      setIsMarketClosed(true);
      setError("Mercado cerrado");
    } else if (isMarketClosed && error === "Mercado cerrado") {
      fetchRate();
    }
  }, [currentTime, isMarketClosed, error]);

  const refreshRate = async () => {
    await fetchRate();
  };

  return { rate, isLoading, refreshRate, error, isMarketClosed };
}
